<template>
  <v-container>
    <v-row justify="center">
      <ViewHeader
        v-if="!isChartFullScreen"
        :title="
          $t('message.reviews.categories.ratingsInCategory', [
            selectedCategoryName
          ])
        "
      >
        <div class="header-actions">
          <ReviewsCategoriesDatePicker @change="handleDateChange" />
          <ui-button
            color="primary"
            class="ml-5"
            icon="icon-map"
            @click="facilitiesDialog = true"
          >
            {{ $t("message.reviews.categories.selectFacilities") }}
          </ui-button>
        </div>
      </ViewHeader>
      <v-col cols="12">
        <div class="company-data-row">
          <div class="company-data">
            <div class="company-data__basic-info">
              <div class="company-data__label">
                <v-icon size="22px" color="primary" class="mr-1">
                  icon-owner </v-icon
                ><span>{{ $t("message.reviews.categories.dataFor") }}:</span>
              </div>
              <div>
                <div class="company-data__title">{{ facilityName }}</div>
                <span class="company-data__secondary-text">
                  <span v-html="selectedFacilitiesDisplayName"></span>
                  <span
                    v-if="getSelectedFacilityMeta.quantity"
                    class="company-data__secondary-quantity"
                  >
                    {{
                      getFacilitiesCountLabel(getSelectedFacilityMeta.quantity)
                    }}</span
                  >
                </span>
              </div>
            </div>
          </div>
          <div class="category-picker">
            <ReviewsCategoriesCategoryPicker
              :tags="tags"
              :placeholder="selectedCategory"
              @get-tags="fetchTags"
              @category-selected="setSelectedCategoryParam"
            />

            <ReviewsCategoriesProcessingTooltip
              v-if="getCategorizationInProgressNumber"
              :label="$t('message.sentiment.reviewsProcessingNumber2')"
            />
          </div>
        </div>

        <ReviewsCategoriesTabsContainer
          :title="
            $t('message.reviews.categories.sentimentRateInPeriod', [
              selectedCategoryName
            ])
          "
          :tabs="chartTabs"
          :groups="chartGroups"
          content-type="chart"
        />

        <ReviewsCategoriesTabsContainer
          :title="$t('message.reviews.categories.facilitiesRatesInPeriodTitle')"
          :tabs="tableTabs"
          :groups="groups"
          content-type="table"
        />
        <FacilitiesDialog
          v-if="facilitiesDialog"
          :show="facilitiesDialog"
          :selected-tab="selectedFacility"
          @selected="handleFacilitySelect"
          @close="facilitiesDialog = false"
        />
      </v-col>
    </v-row>
    <SectionLoader :status="showIndicator" />
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader";
import ReviewsCategoriesDatePicker from "@/components/Reviews/Categories/ReviewsCategoriesDatePicker.vue";
import ReviewsCategoriesCategoryPicker from "@/components/Reviews/Categories/ReviewsCategoriesCategoryPicker.vue";
import FacilitiesDialog from "@/components/Reviews/Categories/FacilitiesDialog.vue";
import ReviewsCategoriesProcessingTooltip from "@/components/Reviews/Categories/ReviewsCategoriesProcessingTooltip.vue";

import SectionLoader from "@/components/Loader/SectionLoader.vue";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { mapActions, mapGetters } from "vuex";
import tagsService from "@/services/tags";
import facilitiesService from "@/services/facilities";
import facilitiesGroupsService from "@/services/facilitiesGroups";
import serviceHandler from "@/helpers/handlers/service";
import { REVIEWS_DATA_PERIOD } from "@/enums/reviews-data-period.enum";
import { categoryAnalyticsMixin } from "@/helpers/mixins/category-analytics";
import cuid from "cuid";
import {
  avgRatingDetailsHeaders,
  reviewsHeaders
} from "@/config/categories-table-columns";
import ReviewsCategoriesTabsContainer from "@/components/Reviews/Categories/ReviewsCategoriesTabsContainer.vue";
import {
  GTM_EVENT_NAME,
  GTM_FACILITY_FILTER_TYPE_ACTION,
  GTM_FACILITY_TAB_NAME
} from "@/enums/gtm-events";

import AnalyticsService from "@/services/analytics";
import { endpoints } from "@/config/endpoints";
const analyticsService = new AnalyticsService(endpoints.analytics.gmb);
const GRID_FILTERS = {
  facility: "facility",
  city: "city",
  facilityGroup: "facility_group"
};

export default {
  name: "ReviewsCategoryDetails",
  components: {
    ViewHeader,
    ReviewsCategoriesDatePicker,
    ReviewsCategoriesCategoryPicker,
    FacilitiesDialog,
    SectionLoader,
    ReviewsCategoriesTabsContainer,
    ReviewsCategoriesProcessingTooltip
  },
  mixins: [gtmEngagementMixin, categoryAnalyticsMixin],
  data() {
    return {
      averageGridSelectedFilter: null,
      reviewsGridSelectedFilter: null,
      showIndicator: false,
      isChartFullScreen: false,
      facilitiesDialog: false,
      selectedDateValues: {},
      selectedCategory: null,
      tags: [],
      REVIEWS_DATA_PERIOD,
      avgRatingHeaders: avgRatingDetailsHeaders(),
      reviewsHeaders: reviewsHeaders(),
      filterTypes: {
        [GTM_FACILITY_TAB_NAME.ALL_LOCATIONS]:
          GTM_FACILITY_FILTER_TYPE_ACTION.ALL_LOCATIONS,
        [GTM_FACILITY_TAB_NAME.CITY]: GTM_FACILITY_FILTER_TYPE_ACTION.CITY,
        [GTM_FACILITY_TAB_NAME.LOCATIONS_GROUP]:
          GTM_FACILITY_FILTER_TYPE_ACTION.LOCATIONS_GROUP,
        [GTM_FACILITY_TAB_NAME.LOCATION]:
          GTM_FACILITY_FILTER_TYPE_ACTION.LOCATION
      },
      reviewsStaticOptions: [
        {
          name: this.$t("message.common.allReviews"),
          id: cuid(),
          allReviews: true
        },
        {
          name: this.$t("message.common.reviewsWithoutCategory"),
          id: cuid(),
          withoutCategory: true
        }
      ],
      chartGroups: [
        {
          name: this.$t("message.reviews.periods.weekly"),
          value: GRID_FILTERS.facility
        },
        {
          name: this.$t("message.reviews.periods.monthly"),
          value: GRID_FILTERS.city
        }
      ],
      groups: [
        { name: this.$t("message.reviews.all"), value: GRID_FILTERS.facility },
        { name: this.$t("message.reviews.cities"), value: GRID_FILTERS.city },
        {
          name: this.$t("message.reviews.facilitiesGroups"),
          value: GRID_FILTERS.facilityGroup
        }
      ]
    };
  },

  computed: {
    selectedCategoryName() {
      return this.selectedCategory ? this.selectedCategory.name : "";
    },
    chartTabs() {
      return [
        {
          id: cuid(),
          text: this.$t("message.reviews.categories.sentiment"),
          name: "sentiment_details",
          component: () =>
            import(
              "@/components/Reviews/Categories/ReviewsCategoriesBarChart.vue"
            ),
          fetchMethod: params => this.getSentimentChartData(params),
          fieldName: "sentiment"
        },
        {
          id: cuid(),
          text: this.$t("message.reviews.categories.generalSentiment"),
          name: "sentiment_general",
          component: () =>
            import(
              "@/components/Reviews/Categories/ReviewsCategoriesLineChart.vue"
            ),
          "max-value": 100,
          "min-value": -100,
          fetchMethod: params =>
            this.getSentimentAggregatedDetailsChart(params),
          fieldName: "sentiment"
        }
      ];
    },
    tableTabs() {
      return [
        {
          id: cuid(),
          text: this.$t("message.reviews.categories.sentiment"),
          name: "sentiment_general",
          component: () =>
            import(
              "@/components/Reviews/Categories/ReviewsCategoriesColorTriadTable.vue"
            ),
          searchParamName: "q",
          searchLabel: this.$t("message.permissions.facilitySearchPlaceholder"),
          fetchMethod: params => this.fetchSentimentData(params)
        },
        {
          id: cuid(),
          name: "sentiment_details",
          text: this.$t("message.reviews.categories.generalSentiment"),
          component: () =>
            import(
              "@/components/Reviews/Categories/ReviewsCategoriesColorScaleTable.vue"
            ),
          searchLabel: this.$t("message.permissions.facilitySearchPlaceholder"),
          searchParamName: "q",
          fetchMethod: params => this.fetchSentimentAggregatedData(params)
        }
      ];
    },
    showGridFilterButtons() {
      return !(
        this.$route.query.city ||
        this.$route.query.facility ||
        this.$route.query.facility_group
      );
    },
    generalParams() {
      return {
        category_id: this.$route.query.categoryId,
        facility: this.$route.query.facility,
        facility_group: this.$route.query.facility_group,
        city: this.$route.query.city,
        withoutCategory: this.$route.query.withoutCategory,
        ...this.selectedFacility.params
      };
    },
    comparisonParams() {
      return {
        ...this.getSelectedFacilityParams,
        ...this.comparisonDate
      };
    },
    facilityName() {
      return this.$store.state.company.company.name;
    },
    isMonthFilterDisabled() {
      if (this.$route.query.dateType === "weeks") {
        return +this.$route.query.dateQuantity <= 4;
      }
      if (this.$route.query.dateType === "days") {
        return +this.$route.query.dateQuantity <= 30;
      }
      if (this.$route.query.dateType === "months") {
        return +this.$route.query.dateQuantity == 1;
      }

      return false;
    }
  },
  async created() {
    const selectedDateValue =
      this.$route.query.date_from && this.$route.query.date_to
        ? {
            date_from: this.$route.query.date_from,
            date_to: this.$route.query.date_to
          }
        : null;
    this.setSelectedDate(selectedDateValue);
    this.clearComparison();
    this.initValues();
    await this.setSelectedCategory();
  },
  mounted() {
    this.mixin__setGtmEngagementTimeouts();
  },
  methods: {
    ...mapActions("reviewsCategoriesAnalytics", [
      "clearComparison",
      "setCurrentSelectedCategory"
    ]),

    async filterData(current) {
      this.showIndicator = true;
      this.facilitiesDialog = false;

      await this.fetchReviewsData();
      this.showIndicator = false;
    },
    async fetchReviewsData(params) {
      const [{ results, count }, pinnedData] = await Promise.all([
        analyticsService.getReviewsDetailsTable(params),
        analyticsService.getRatingsDetailsPinnedTable(params)
      ]);
      return {
        results: [...pinnedData, ...results],
        count
      };
    },
    async fetchAvgReviewsData(params) {
      const [{ results, count }, pinnedData] = await Promise.all([
        analyticsService.getAverageRatingsDetailsTable(params),
        analyticsService.getAverageRatingsDetailsPinnedTable(params)
      ]);

      return {
        results: [...pinnedData, ...results],
        count
      };
    },

    async fetchSentimentData(params) {
      const { results, count } = await analyticsService.getSentimentTable(
        params
      );

      return {
        results,
        count
      };
    },

    async fetchSentimentAggregatedData(params) {
      const {
        results,
        count
      } = await analyticsService.getSentimentAggregatedTable(params);
      return {
        results,
        count
      };
    },
    isAverageGridOptionChecked(optionName) {
      return this.averageGridSelectedFilter === optionName ? "primary" : "";
    },
    isReviewsGridOptionChecked(optionName) {
      return this.reviewsGridSelectedFilter === optionName ? "primary" : "";
    },
    async setSelectedCategory() {
      if (!this.$route.query.tag_id && !this.$route.query.withoutCategory) {
        this.selectedCategory = this.reviewsStaticOptions[0];
      } else if (this.$route.query.withoutCategory) {
        this.selectedCategory = this.reviewsStaticOptions[1];
      } else {
        this.selectedCategory = await serviceHandler(
          tagsService.getTag(this.$route.query.tag_id)
        );
      }

      this.setCurrentSelectedCategory(this.selectedCategory);
    },
    async setSelectedCategoryParam({
      id: categoryId,
      allReviews,
      withoutCategory
    }) {
      let query = { ...this.$route.query };
      delete query.allReviews;
      delete query.withoutCategory;

      if (!allReviews && !withoutCategory) {
        this.$router.push({ query: { ...query, tag_id: categoryId } });
        await this.setSelectedCategory();
        return;
      }

      if (allReviews) {
        delete query.tag_id;
        this.$router.push({ query });
      } else if (withoutCategory) {
        query = { ...query, withoutCategory: true };
        delete query.tag_id;
        this.$router.push({ query });
      }
      this.initValues();
      await this.setSelectedCategory();
    },
    async handleDateChange({ date_from, date_to, comparison, period_type }) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.FILTER_DATE_PERIOD_V2,
        date_from,
        date_to,
        period_type
      });
      this.setDateChangeData(date_from, date_to, comparison, period_type);
    },

    async fetchChartData({ view, fieldName, ...rest }, fetchMethodCallback) {
      if (!this.comparisonDate) {
        const basic = await fetchMethodCallback({
          ...(this.selectedDate && this.selectedDate),
          ...this.generalParams,
          view,
          fieldName,
          ...rest
        });
        return { basic, comparison: [] };
      }

      const [basic, comparison] = await Promise.all([
        fetchMethodCallback({
          ...(this.selectedDate && this.selectedDate),
          ...this.generalParams,
          view,
          fieldName,
          ...rest
        }),
        fetchMethodCallback({
          ...(this.comparisonDate && this.comparisonDate),
          ...this.generalParams,
          view,
          fieldName,
          ...rest
        })
      ]);
      return {
        basic,
        comparison
      };
    },
    async getSentimentAggregatedDetailsChart(view) {
      return this.fetchChartData(
        { ...view, category_id: this.$route.query.tag_id },
        params => analyticsService.getSentimentAggregatedDetailsChart(params)
      );
    },

    async getSentimentChartData(view) {
      return this.fetchChartData(
        { ...view, category_id: this.$route.query.tag_id },
        params => analyticsService.getSentimentDetailsChart(params)
      );
    },

    async fetchTags() {
      this.tags = [...(await serviceHandler(tagsService.getTags()))];
    },

    async handleFacilitySelect(facility) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.FILTER_LOCATIONS_V2,
        filter_type: this.filterTypes[facility.tab] || "all_locations",
        num_selected: 1
      });
      this.setFacilitySelectData(facility);
    },

    async getLocalSelectedFacility(itemDetails) {
      const selectedFacility = {};

      selectedFacility.id =
        itemDetails.facility || itemDetails.facility_group || itemDetails.city;
      selectedFacility.params = itemDetails;
      if (itemDetails.facility) {
        const { data } = await facilitiesService.getFacilityData(
          itemDetails.facility
        );
        selectedFacility.tab = 3;
        selectedFacility.meta = {
          type: "facility",
          displayName: data.name,
          storeCode: data.store_code,
          address: data.formatted_address,
          quantity: 0
        };
      }
      if (itemDetails.facility_group) {
        selectedFacility.tab = 2;
        const {
          data: { results }
        } = await facilitiesGroupsService.getFacilitiesGroups({
          groups: [itemDetails.facility_group]
        });
        selectedFacility.meta = {
          type: "facility_group",
          displayName: results[0].name,
          quantity: results[0].facilities_count
        };
      }
      if (itemDetails.city) {
        selectedFacility.tab = 1;
        selectedFacility.meta = {
          type: "city",
          displayName: itemDetails.city,
          quantity: 0
        };
      }

      return selectedFacility;
    }
  }
};
</script>

<style lang="scss" scoped>
.category-picker {
  display: flex;
  align-items: center;
  gap: $spacing;
  flex-shrink: 0;
}
::v-deep .separator {
  margin: 0 $spacing * 0.75;
  letter-spacing: 0;
}
.loader {
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.header-actions {
  display: flex;
  flex-grow: 1;
  align-items: center;
  padding-left: $spacing * 2;
  justify-content: flex-end;
}

::v-deep .card__indicator {
  &.increasing {
    * {
      color: #237772 !important;
    }
  }
  &.decreasing {
    * {
      color: #d4295c !important;
    }
  }
}

.company-data {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &-row {
    display: flex;
    justify-content: flex-start;
    gap: $spacing * 4;
    margin: $spacing * 2 0 $spacing * 4 0;
  }

  &__basic-info {
    display: flex;
    align-items: center;
  }

  &__label {
    color: $primary;
    margin-right: $spacing * 1.5;
  }

  &__title {
    @include font-size(16px);
    line-height: 1rem;
  }

  &__secondary-text {
    @include font-size(14px);
    font-weight: 400;
  }
  &__secondary-quantity {
    @include font-size(14px);
    font-weight: 400;
    color: $primary;
  }
}
.tabs-actions {
  margin-bottom: $spacing * 5;
  display: flex;
  justify-content: flex-end;

  > * + * {
    margin-left: $spacing;
  }

  ::v-deep .v-btn {
    text-transform: none;
    letter-spacing: 0;
  }
}
</style>
